import { libraryIconComponent } from '../common/LibraryIcon';
import { pairRangeEntries } from '../../utils/dateBounds';
import { formatDisplayDateTime, formatDisplayTime, formatShortDateByPrecision, formatShortDateTime } from '../../utils/dateDisplay';
import { earliestDateValue, formatDateValue, precisionOf, summarizeDateValues } from '../../utils/dateOptions';
import { formatMeasurement } from '../../utils/units';
import { displayPrefsFor } from '../../utils/measurements';
import { formatCurrency } from '../../utils/currency';
import { formatBooleanValue, numberFormatOptions } from '../../utils/elements';
import { formatNumber } from '../../utils/elementFormat/number';
import { formatEmail } from '../../utils/elementFormat/email';
import { formatLocation } from '../../utils/elementFormat/location';
import { formatPhone } from '../../utils/elementFormat/phone';
import { resolveLookupText } from '../../utils/lookups';
import { formatRating } from '../../utils/rating';
import { choiceLabelsText, choiceListOf } from '../../utils/choiceValues';
import { readCount } from '../../utils/confirmationValues';
import { entryCountWords } from '../../utils/repeatingGroupEntries';
import { TILE_COLS, readTileBody } from '../../config/tileSlotPolicy';
import { MASK_TEXT, isMaskedElement } from '../../utils/maskedElements';

/**
 * Shared value-display helpers for every surface that renders item element
 * values (the uniform cards, the detail pane, the table view).
 */

/**
 * A stored value's choice — its label, colour and icon — on a Choice element.
 * Nothing on any other type: a list a short text (or any type) carries is
 * inert, kept for a change back to Choice (KPE-033), so its value reads as the
 * text it is.
 */
export const getChoiceMeta = (element, rawValue) => {
	const choices = choiceListOf(element);
	if (!choices.length) {
		return { color: null, icon: null, label: null };
	}
	const found = choices.find(choice => choice?.value === rawValue);
	if (!found) return { color: null, icon: null, label: null };
	// A library icon, drawn on demand (KPR-1187); null for a name the library does not have.
	const IconComponent = found.icon ? libraryIconComponent(found.icon) : null;
	return {
		color: found.color || null,
		icon: IconComponent || null,
		label: found.label || null,
	};
};

const measurementPrefs = (prefs) => displayPrefsFor(prefs?.unitSystem);

/**
 * The display text for one element value — the platform's ONE reading of a
 * stored value by its dataType, the web's counterpart to the API's
 * utils/export/valueFormat.js. Every surface that prints a value should come
 * here rather than compose getChoiceMeta + a lookup resolver + this function
 * by hand, because the surfaces that composed it by hand are exactly the ones
 * that printed slugs and raw ids.
 *
 * The third argument is the display context:
 *
 *   - `unitSystem` is the per-user preference the measurement formatter reads
 *     (hooks/useUnitSystem at the component call sites; the shared formatter
 *     is a plain function, so the surfaces subscribe and pass it through).
 *     Absent = the card's own display rules.
 *   - `resolveLookupValue` is the item pool a card-lookup needs to become a
 *     title (utils/lookups createLookupResolver, or the shared page's title
 *     map). Absent, a reference renders as nothing rather than as its id.
 */
export const formatValue = (element, rawValue, prefs = undefined) => {
	if (rawValue === undefined || rawValue === null || rawValue === '') return '';
	// A MASKED element (KPR-1348, utils/maskedElements.js) reads as its
	// fixed-length mask unless the caller asks for the value (`reveal: true`
	// — components/common/MaskedValue.js, behind Show and Copy). Masked by
	// default, so a surface that prints values without knowing about masks
	// (a home tile, a query block, a draft's diff, a printed page) never
	// prints the code.
	if (prefs?.reveal !== true && isMaskedElement(element)) return MASK_TEXT;
	switch (element?.dataType) {
		// The author's own word for the stored value ("CDL A", not "cdl-a").
		// A value whose choice was since deleted still reads as it is stored,
		// which is what the API's exporter does with the same value.
		case 'choice':
			// A Choice that allows multiple values holds a LIST: every label,
			// in the choices' order, joined like every other list (utils/
			// choiceValues). A list left on a choice whose "Allow multiple" was
			// turned off reads the same way, so an option change never prints
			// an array.
			if (Array.isArray(rawValue)) return choiceLabelsText(element, rawValue);
			return getChoiceMeta(element, rawValue).label || rawValue;
		// A reference is the referenced record's TITLE or it is nothing. An id
		// the pool cannot resolve is OMITTED rather than printed — the API's
		// rule (valueFormat lookupText), and the reason a worklist row read
		// "6aacc1f8… — post-trip". The omit itself lives in utils/lookups so
		// that a value-only page block, a labelled field row and a printed
		// panel cell cannot disagree about the same unresolved id.
		case 'card-lookup':
			return resolveLookupText(prefs?.resolveLookupValue, rawValue);
		// The element's own two words ("Paid" / "Unpaid"), never true/false.
		// FALSE reaches here: it is a value, not a blank.
		case 'boolean':
			return formatBooleanValue(element, rawValue) || '';
		// Fixed decimals and leading zeros, through the module the API prints
		// with (utils/elementFormat/number), so a padded id reads the same in
		// an export, a title and this cell. A value the formatter cannot read
		// (legacy text under a numeric element) is shown as it is stored
		// rather than blanked.
		case 'number':
		case 'decimal':
		case 'integer':
			return formatNumber(rawValue, numberFormatOptions(element?.dataType, element?.options)) || rawValue;
		// E.164 is the storage spelling, not a legible one: a number reads
		// the way its element's country dials it (national at home,
		// international abroad), through the module the API renders with.
		case 'phone':
			return formatPhone(rawValue, element?.options?.country);
		// An address is what a person recognises, so it wins over the
		// coordinates; a value with only a point reads as "37.7749,
		// -122.4194". A location is an OBJECT, so nothing below could have
		// rendered it — the default case would hand back [object Object].
		case 'location':
			return formatLocation(rawValue);
		// An address reads exactly as it is stored; formatEmail exists so
		// every type has one formatter with one signature.
		case 'email':
			return formatEmail(rawValue);
		// One entry or several (utils/dateOptions `allowMultiple`), read at the
		// element's own precision and joined ascending: "Sep 9, 2026, Sep 16,
		// 2026". Both mismatched shapes still render — a list under an element
		// whose multi was turned off, a scalar under one where it was turned
		// on — so an option change never blanks a column.
		case 'date':
		case 'date-time':
			return formatDateValue(element, rawValue);
		// A time is stored 'HH:mm', which dayjs does not read on its own —
		// without this case the list printed the raw stored string (and, for
		// values the old input wrote, a whole ISO timestamp).
		case 'time':
			return formatDisplayTime(rawValue);
		// Stars, a "½" for a half star, and the number out of the scale when
		// the scale is not the default five (utils/rating formatRating) —
		// the export's "7.5 / 10", beside what a person reads at a glance.
		case 'rating':
			return formatRating(rawValue, element?.options);
		// The stored { value, unit, base } (or a legacy bare number, read in
		// the default unit) through the element's display rules — "8 lb 7 oz",
		// "150 lb" — unless `prefs.unitSystem` is metric/imperial, which swaps
		// in the measure's system default unit and skips the threshold rules
		// (docs/plans/measurements.md decision #9).
		case 'measurement':
			return formatMeasurement(rawValue, element?.options || {}, measurementPrefs(prefs));
		// The stored { amount, currency } (or a legacy bare number, read as
		// major units in the default currency) through the shared formatter:
		// "$1,234.50", "CA$12.00", "¥1,235" — or "USD 1,234.50" when the
		// element shows codes (docs/plans/currency.md decision 5).
		case 'currency':
			return formatCurrency(rawValue, element?.options || {});
		// A user value's TEXT is what the value holds: ids on an authenticated
		// surface (which renders them as people through UserValue before it
		// ever gets here), display names on a public page (the API replaces
		// the ids). A list joins like every other list.
		case 'user':
			return Array.isArray(rawValue) ? rawValue.map(entry => String(entry ?? '')).filter(Boolean).join(', ') : String(rawValue);
		// A file's stored value is an attachment id, which never reads as
		// text: the surfaces that show a file draw it from the item read's
		// `files` facts (components/common/FileValue.js), and a surface that
		// can only print text prints nothing rather than an id.
		case 'file':
			return '';
		// How many people said "me too" (KPR-390): a whole number, as the
		// API's export prints it. A value that is not one reads as 0, the
		// API's readCount. (Absent never reaches here: it returned '' above,
		// and a surface that prints 0 for it says so itself — the table.)
		case 'confirmations':
			return readCount(rawValue).toLocaleString();
		// A repeating group (KPR-680) is a LIST OF ENTRIES, never one value: a
		// surface that can only print text (a tile, a cell, a title, a conflict
		// dialog) says how many there are. The item form and read view draw the
		// entries themselves (RepeatingGroupField, RepeatingGroupValue).
		case 'repeating-group':
			return entryCountWords(rawValue);
		default:
			return rawValue;
	}
};

// Measurement and currency count as numeric: they lead a card's fields after
// the required ones and render at reading size, like any other quantity. So
// does a "Me too" count (KPR-390) — it is the number people scan a list for.
const NUMERIC_TYPES = new Set(['number', 'decimal', 'integer', 'rating', 'measurement', 'currency', 'confirmations']);
const LONG_TEXT_TYPES = new Set(['text-large', 'rich-text']);

export const isNumericType = dataType => NUMERIC_TYPES.has(dataType);
export const isLongTextType = dataType => LONG_TEXT_TYPES.has(dataType);

const ownLabel = element => element?.label?.singular || element?.name || '';

/**
 * A date range's one label on the surfaces that draw the pair as ONE value
 * (the tile, the read view): the two labels as authored, joined — "Departs /
 * Returns" (KPR-626, the owner's k05). The form draws two fields and keeps
 * each its own.
 */
export const rangeLabel = (start, end) => [ownLabel(start), ownLabel(end)].filter(Boolean).join(' / ');

/**
 * An element's label. A tile's range field (`rangeEndDef`, pickDisplayFields
 * and resolveTileLayout) is labelled with both halves.
 */
export const elementLabel = element =>
	(element?.rangeEndDef ? rangeLabel(element, element.rangeEndDef) : ownLabel(element));

/**
 * Which of a card's fields a uniform item card shows, per the platform
 * spec: text cards (a long-text field leads) render that one field as a
 * 3-line-clamped body; structured cards show up to `max` highest-value
 * fields side by side — required fields first, then numeric, then
 * definition order.
 *
 * Returns { mode: 'text', field } or { mode: 'fields', fields: [...] }.
 */
export const pickDisplayFields = (elementDefs, max = 3, { exclude = null } = {}) => {
	// A range takes ONE slot, not two: the start carries its end
	// (`rangeEndDef`), labelled with both ("Departs / Returns"), and the end
	// element has no slot of its own, so a tile never spends two of its three
	// fields on the halves of one span.
	//
	// `exclude` names the card's primary-date element, which the card's
	// header already shows, and drops it. A range is the exception: when the
	// primary date IS a range start the merged field still comes back (the
	// owner's k04, KPR-626) — a range reads the same on every tile, and its
	// length is worth the slot even though the header names the day it began.
	const defs = [];
	pairRangeEntries((elementDefs || []).filter(def => def?.name)).forEach(slot => {
		if (slot.entry.name === exclude && !slot.end) return;
		defs.push(slot.end ? { ...slot.entry, rangeEndDef: slot.end } : slot.entry);
	});
	if (!defs.length) return { mode: 'fields', fields: [] };

	const required = defs.filter(def => def?.options?.required);
	const numeric = defs.filter(def => !def?.options?.required && isNumericType(def?.dataType));
	const rest = defs.filter(def => !def?.options?.required && !isNumericType(def?.dataType));
	const prioritized = [...required, ...numeric, ...rest];

	const lead = prioritized[0];
	if (lead && isLongTextType(lead.dataType)) {
		return { mode: 'text', field: lead };
	}
	// Long-text fields never work as side-by-side card fields; the pane
	// shows them in full.
	const fields = prioritized.filter(def => !isLongTextType(def.dataType)).slice(0, max);
	if (!fields.length && lead) return { mode: 'text', field: lead };
	return { mode: 'fields', fields };
};

/**
 * The fields a TILE can hold, by name: every element, except that a date
 * range is ONE field — its start, carrying its end (`rangeEndDef`) — found
 * under either half's name, so a layout that placed the end reaches the same
 * field (KPR-626). In definition order; the tile editor's palette and its
 * reading of a stored layout use the same map the renderer does.
 */
export const tileFieldsByName = (elementDefs) => {
	const byName = new Map();
	pairRangeEntries((elementDefs || []).filter(def => def?.name)).forEach(slot => {
		const field = slot.end ? { ...slot.entry, rangeEndDef: slot.end } : slot.entry;
		byName.set(slot.entry.name, field);
		if (slot.end) byName.set(slot.end.name, field);
	});
	return byName;
};

/**
 * The fields a tile shows when a stored layout says so — the same reading
 * `pickDisplayFields` returns, so the tile renders one shape either way.
 *
 * With NO layout this is exactly the automatic pick every card has today, so
 * an unconfigured card is untouched. With one, the layout is the whole
 * answer:
 *
 *   - a long-text or rich-text placement ALONE in the body at all six
 *     columns comes back as `{ mode: 'text' }`, the clamped-paragraph
 *     reading the automatic picker already produces for a text-led card
 *     (every body stored before long text could share a row is this);
 *   - anything else comes back as `{ mode: 'rows' }` — rows of
 *     `{ field, span }`, each span a share of the tile's six columns.
 *
 * The body is read through tileSlotPolicy readTileBody (KPR-346, KPR-761): a
 * body in sixths is the answer; one that is not (none is stored any more) is
 * no layout, and the card gets the automatic pick, never a tile drawn at the
 * wrong proportions.
 *
 * `exclude` (the card's primary-date element, which the header shows) is
 * honoured only by the automatic pick. A layout that NAMES a date has asked
 * for it, and under an ordered primary date which element heads a tile is a
 * per-item answer anyway — so an explicit layout renders what it names.
 *
 * An element the layout names that the card no longer has is dropped, and a
 * layout left holding nothing renders nothing: an author who cleared the body
 * meant it, and falling back to the automatic pick would resurrect fields
 * they had removed.
 */
export const resolveTileLayout = (layout, elementDefs, { max = 3, exclude = null } = {}) => {
	const rows = readTileBody(layout?.body)?.rows;
	if (!Array.isArray(rows)) return pickDisplayFields(elementDefs, max, { exclude });

	// A span is ONE field wherever it is read (docs/platform-standards.md §4,
	// KPR-626): a placed half of a range — the start or the end — draws the
	// whole range, labelled with both, exactly as the automatic pick gives it.
	// It is drawn ONCE, at the first placement in reading order; a layout that
	// places the other half too (every tile arranged before KPR-626 could) has
	// that placement dropped, and nothing stored is rewritten.
	const byName = tileFieldsByName(elementDefs);
	const drawn = new Set();

	const resolvedRows = rows
		.map(row => (Array.isArray(row) ? row : [])
			.map(placement => {
				const field = byName.get(placement?.element);
				if (!field) return null;
				if (field.rangeEndDef) {
					if (drawn.has(field.name)) return null;
					drawn.add(field.name);
				}
				const span = Number.isInteger(placement?.span) ? placement.span : 1;
				return { field, span };
			})
			.filter(Boolean))
		.filter(row => row.length);

	const flat = resolvedRows.flat();
	if (flat.length === 1 && isLongTextType(flat[0].field.dataType) && flat[0].span >= TILE_COLS) {
		return { mode: 'text', field: flat[0].field };
	}
	return { mode: 'rows', rows: resolvedRows };
};

/**
 * An item's primary date for reading (utils/cards.js primaryDateForChain):
 * the created / updated timestamp in the viewer's date-time format, or a
 * date element through the same formatter every other cell uses. `short`
 * is the tile's spelling — "Sep 9" for a date, "Sep 9, 3:15 PM" with a
 * time — a label, so it does not follow the date-format preference. Under
 * an ORDERED spec pass the per-item answer (utils/cards.js
 * resolveItemPrimaryDate) and its value: which entry supplied the date is
 * what decides the formatter.
 */
export const formatPrimaryDate = (spec, rawValue, { short = false } = {}) => {
	if (rawValue === undefined || rawValue === null || rawValue === '') return '';
	// A primary date is ONE date, so a multi element contributes its EARLIEST
	// entry — the day the thing began, which is what a header, a day group and
	// a sort are all asking for. (`getItemPrimaryDate` already reduces it; this
	// repeats the reduction so a caller reading the raw field cannot print a
	// whole list into a header.)
	const value = spec?.kind === 'element' && Array.isArray(rawValue)
		? earliestDateValue(spec?.dataType || spec?.element?.dataType, rawValue)
		: rawValue;
	if (value === undefined || value === null || value === '') return '';
	if (short) {
		// At the element's own PRECISION, exactly as the long reading below is:
		// a tile whose header said "Sep 1" while its tooltip said "September
		// 2026" would be showing a day the author never chose, and contradicting
		// itself in the same hover. `formatShortDateByPrecision` IS
		// `formatShortDate` at 'day', so every other element is unchanged.
		return spec?.dataType === 'date'
			? formatShortDateByPrecision(value, precisionOf(spec?.element))
			: formatShortDateTime(value);
	}
	if (spec?.kind === 'element') return formatValue(spec.element, value) || '';
	return formatDisplayDateTime(value);
};

/**
 * The tile's reading of a date element: one date, or the earliest plus how
 * many more ("Sep 9 +2"), with the whole list for the tooltip. Null when
 * there is only one date to show — the caller prints that the ordinary way.
 */
export const summarizeMultiDate = (element, rawValue) => {
	// A date list only: a choice that allows multiple values is a list too
	// (data types PR 5), and reading its slugs as dates printed "+2" and no
	// date. It has its own reading, ChoiceChips.
	if (!Array.isArray(rawValue) || !['date', 'date-time'].includes(element?.dataType)) return null;
	const summary = summarizeDateValues(element, rawValue);
	return summary && summary.extra > 0 ? summary : null;
};
